/**
 * Who, of the people on a job, does what. Decision 84.
 *
 * A job contact is one row per person. What they are to the job is a set of
 * roles — the job contact deals with us about the work, the site contact is
 * who we meet at the property, a custom role is whatever the office calls it —
 * and two flags: `isBilling`, held by at most one person, and
 * `receivesComms`. This is the only place those become a recipient, so the
 * card, the send sheet and `resolveRecipient` cannot disagree about who gets
 * the invoice.
 *
 * One person on a job is everyone. Nobody marks the only contact as billing,
 * and the invoice going to `client.email` instead of them would be a surprise.
 */

/** The roles the app offers. The enum's other values are pre-2026-09-23. */
export const JOB_CONTACT_ROLES = [
  { key: 'JOB', label: 'Job', hint: 'Deals with us about the work' },
  { key: 'SITE', label: 'Site', hint: 'Who we meet at the property' },
] as const

export type JobContactRoleKey = (typeof JOB_CONTACT_ROLES)[number]['key'] | 'OTHER'

export interface JobContactLike {
  id: string
  roles: readonly string[]
  label: string | null
  isBilling: boolean
  receivesComms: boolean
  contact: { email: string | null; optedOutOfNotifications?: boolean }
}

export interface JobContactDuties<T> {
  /** Who the invoices go to: the one marked, else the only person on the job. */
  billing: T | null
  /** Who to ring about the work: the first with `JOB`, else the only person. */
  job: T | null
  /** Everyone who hears about the job, less anyone who has opted out. */
  comms: T[]
  /** Exactly one person on the job, who is therefore all of the above. */
  sole: boolean
}

export function jobContactDuties<T extends JobContactLike>(
  contacts: readonly T[],
): JobContactDuties<T> {
  const sole = contacts.length === 1
  const only = sole ? contacts[0]! : null

  return {
    billing: contacts.find((link) => link.isBilling) ?? only,
    job: contacts.find((link) => link.roles.includes('JOB')) ?? only,
    comms: contacts.filter((link) => link.receivesComms && !link.contact.optedOutOfNotifications),
    sole,
  }
}

/**
 * The chips a person wears on this job, in a fixed order: Job, Billing, Site,
 * then the custom label. The only person on a job wears Job and Billing
 * whether or not anybody ticked them, because that is what they are.
 */
export function jobContactTags(link: JobContactLike, sole = false): string[] {
  const tags: string[] = []

  if (sole || link.roles.includes('JOB')) tags.push('Job')
  if (sole || link.isBilling) tags.push('Billing')
  if (link.roles.includes('SITE')) tags.push('Site')
  if (link.roles.includes('OTHER')) tags.push(link.label?.trim() || 'Other')

  return tags
}

/**
 * People who belong to the client, rather than to one property.
 *
 * A contact with no site is who the office deals with about the account.
 * Most jobs need only these. A contact with a site is who we meet at that
 * property, and is offered on a job only when the job is there. Decision 85.
 */
export function clientContacts<T extends { siteId: string | null }>(contacts: readonly T[]): T[] {
  return contacts.filter((contact) => contact.siteId == null)
}

/** The people at one property. */
export function siteContacts<T extends { siteId: string | null }>(
  contacts: readonly T[],
  siteId: string,
): T[] {
  return contacts.filter((contact) => contact.siteId === siteId)
}

/**
 * Whether a contact's `position` is only ServiceM8's job-contact type.
 *
 * The loader used to copy the type into `position`, so thousands of imported
 * people read "JOB · 0407…". That word is now a chip; drawing it again as a
 * job title is the duplication this replaced.
 */
export function positionIsRoleWord(position: string | null | undefined): boolean {
  if (!position) return false

  return /^(job|billing|site|property manager|tenant)$/i.test(position.trim())
}
